// src/utils/pipelineView.js
// Pure helpers for the pipeline views. No React, no Firestore — data in,
// view-model out.
// Blend-view spec: docs/superpowers/specs/2026-07-15-pipeline-blend-redesign-design.md
// Board|List spec: docs/superpowers/specs/2026-07-28-pipeline-board-list-redesign.md

import { parseLocalDate } from "./statusSheetData";

// A lead needs attention when it's actively being worked but has NO next touch
// on the calendar — no follow-up date and no appointment. Parked leads
// (Deferred / Next Year) are deliberately waiting, so they never count.
export const needsAttentionLead = (lead, { parkingStages = [] } = {}) =>
  String(lead?.status || "active").toLowerCase() === "active" &&
  !parkingStages.includes(lead?.stage || "") &&
  !String(lead?.nextFollowUp || "").trim() &&
  !String(lead?.appointmentAt || "").trim();

// The lead's next touch as a sortable time (appointment wins over follow-up);
// leads with nothing scheduled sort last within their group.
export const nextTouchTime = (lead) => {
  const dt =
    parseLocalDate(lead?.appointmentAt) || parseLocalDate(lead?.nextFollowUp);
  return dt ? dt.getTime() : Infinity;
};

// Build the blend view-model:
//   needs   — active leads with no next touch (the red band; All-stages view only)
//   groups  — [{stage, rows}] for non-parking stages, EXCLUDING the needy rows
//             (they show only in the band — no duplicates), sorted by next touch
//   waiting — leads in parking stages (compact "Waiting" band)
//   counts  — per-stage totals INCLUDING needy/parked (the strip numbers)
export function groupPipelineView(leads, { stages = [], parkingStages = [] } = {}) {
  const list = Array.isArray(leads) ? leads : [];
  const stageOf = (l) => l?.stage || stages[0] || "";
  const counts = {};
  stages.forEach((s) => {
    counts[s] = list.filter((l) => stageOf(l) === s).length;
  });

  const needs = list
    .filter((l) => needsAttentionLead(l, { parkingStages }))
    .sort((a, b) => nextTouchTime(a) - nextTouchTime(b));
  const needsIds = new Set(needs.map((l) => l.id));

  const groups = stages
    .filter((s) => !parkingStages.includes(s))
    .map((stage) => ({
      stage,
      rows: list
        .filter((l) => stageOf(l) === stage && !needsIds.has(l.id))
        .sort((a, b) => nextTouchTime(a) - nextTouchTime(b)),
    }))
    .filter((g) => g.rows.length > 0 || needs.some((l) => stageOf(l) === g.stage));

  const waiting = list
    .filter((l) => parkingStages.includes(stageOf(l)))
    .sort((a, b) => nextTouchTime(a) - nextTouchTime(b));

  return { needs, groups, waiting, counts, total: list.length };
}

// Local calendar date ("YYYY-MM-DD") n days from today — the shared "snooze a
// week out" math for both pipelines. Local time on purpose: a UTC-based date
// would land on the wrong day in Central time.
export const isoDaysFromNow = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// Case-insensitive client-name compare; unnamed leads sort last in ascending.
const compareClientNames = (a, b) => {
  const an = String(a?.contactName || "").trim();
  const bn = String(b?.contactName || "").trim();
  if (!an && !bn) return 0;
  if (!an) return 1;
  if (!bn) return -1;
  return an.localeCompare(bn, undefined, { sensitivity: "base" });
};

// Within-a-stage row order for the Board view: attention leads first, then by
// next touch ascending, then name A-Z.
const compareBoardRows = (parkingStages) => (a, b) => {
  const an = needsAttentionLead(a, { parkingStages }) ? 0 : 1;
  const bn = needsAttentionLead(b, { parkingStages }) ? 0 : 1;
  if (an !== bn) return an - bn;
  const at = nextTouchTime(a);
  const bt = nextTouchTime(b);
  if (at !== bt) return at - bt;
  return compareClientNames(a, b);
};

// Board view-model: one column per stage — known stages in `stages` order with
// parked (parking) stages moved to the end, then unknown stages appended in
// first-seen order. Empty stages still get a column. Stage-less leads land in
// the first stage (same convention as groupPipelineView).
export function groupLeadsByStage(leads, stages = [], { parkingStages = [] } = {}) {
  const list = Array.isArray(leads) ? leads : [];
  const stageOf = (l) => l?.stage || stages[0] || "";

  const known = new Set(stages);
  const unknown = [];
  list.forEach((l) => {
    const s = stageOf(l);
    if (!known.has(s) && !unknown.includes(s)) unknown.push(s);
  });

  const ordered = [
    ...stages.filter((s) => !parkingStages.includes(s)),
    ...stages.filter((s) => parkingStages.includes(s)),
    ...unknown,
  ];

  const rowSort = compareBoardRows(parkingStages);
  return ordered.map((stage) => ({
    stage,
    parked: parkingStages.includes(stage),
    rows: list.filter((l) => stageOf(l) === stage).sort(rowSort),
  }));
}

// List view sorting. Keys:
//   "attention" (default) — attention leads first, then next touch ascending
//   "client"              — client name A-Z
//   "stage"               — position in `stages` (unknown stages last in asc)
//   "nextTouch"           — next touch date (nothing scheduled last in asc)
// `dir` is "asc" | "desc". Stable (ties keep input order in both directions),
// never mutates the input.
export function sortLeadsForList(
  leads,
  key = "attention",
  dir = "asc",
  { stages = [], parkingStages = [] } = {},
) {
  const list = Array.isArray(leads) ? leads : [];
  const sign = dir === "desc" ? -1 : 1;

  const stageIndex = (l) => {
    const i = stages.indexOf(l?.stage || stages[0] || "");
    return i === -1 ? Infinity : i;
  };
  const attentionCompare = (a, b) => {
    const an = needsAttentionLead(a, { parkingStages }) ? 0 : 1;
    const bn = needsAttentionLead(b, { parkingStages }) ? 0 : 1;
    if (an !== bn) return an - bn;
    return nextTouchTime(a) - nextTouchTime(b);
  };
  const compareBy = {
    attention: attentionCompare,
    client: compareClientNames,
    stage: (a, b) => stageIndex(a) - stageIndex(b),
    nextTouch: (a, b) => nextTouchTime(a) - nextTouchTime(b),
  };
  const cmp = compareBy[key] || attentionCompare;

  return list
    .map((l, i) => ({ l, i }))
    .sort((x, y) => {
      const c = cmp(x.l, y.l);
      // c is NaN when both sides are "missing" (Infinity - Infinity); treat
      // that and 0 as ties and fall back to input order for stability.
      return c && !Number.isNaN(c) ? sign * c : x.i - y.i;
    })
    .map((x) => x.l);
}

// Focused view for one stage: everyone in it, the needy ones first, then by
// next touch date.
export function focusStageRows(leads, stage, { parkingStages = [] } = {}) {
  const list = Array.isArray(leads) ? leads : [];
  return list
    .filter((l) => (l?.stage || "") === stage || (!l?.stage && stage === "New Lead"))
    .sort((a, b) => {
      const an = needsAttentionLead(a, { parkingStages }) ? 0 : 1;
      const bn = needsAttentionLead(b, { parkingStages }) ? 0 : 1;
      return an - bn || nextTouchTime(a) - nextTouchTime(b);
    });
}
